import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';

import baseSubdivisions from '../generated/shared_data/geo/iso3166-2.json';
import subdivisionOverrides from '../generated/shared_data/geo/subdivision_overrides.json';
import subdivisionAliases from '../generated/shared_data/geo/subdivision_aliases.json';
import pluck from '@watershed/shared-util/src/pluck';
import keyBy from 'lodash/keyBy';
import { WS_ISO3166_COUNTRY_BY_ALPHA_2, getCountryName } from './countries';
import { normalizeLocationRef } from './utils';

// Data format of the raw subdivisions data from JSON
interface IRawSubdivisionData {
  code: string; // ISO3166-2
  name: string;
  type?: string;
}

export interface ISubdivisionData extends IRawSubdivisionData {
  aliases?: ReadonlyArray<string>;
}

function getAllSubdivisions(): Array<ISubdivisionData> {
  const aliasMap = groupBy(subdivisionAliases, (a) => a.code);
  const results: Array<ISubdivisionData> = [];

  for (const subdivision of baseSubdivisions) {
    // If we override the subdivision to a country, ignore it.
    if (subdivision.code in subdivisionOverrides) {
      continue;
    }

    results.push({
      ...subdivision,
      aliases: aliasMap[subdivision.code]?.map((a: { name: string }) => a.name),
    });
  }
  return results;
}

// Metadata about all the subdivisions supported by Watershed.
export const WS_ISO3166_SUBDIVISIONS: Array<ISubdivisionData> =
  getAllSubdivisions();

export const WS_ISO3166_SUBDIVISIONS_NAMES: Array<string> = uniq(
  WS_ISO3166_SUBDIVISIONS.map(pluck('name'))
);

export const WS_ISO3166_SUBDIVISIONS_BY_NAME: Record<
  string,
  Array<ISubdivisionData> | undefined
> = groupBy(WS_ISO3166_SUBDIVISIONS, (s) => s.name);

export const WS_ISO3166_SUBDIVISIONS_BY_CODE: Record<string, ISubdivisionData> =
  keyBy(WS_ISO3166_SUBDIVISIONS, (s) => s.code);

// Some subdivisions are overridden to be countries. We want to inform the
// user when they should adjust their data in this way, and support references
// by name and by code.
const overriddenSubdivisionInfo = Object.entries(subdivisionOverrides).flatMap(
  ([subdivisionCode, { country, names }]) =>
    names.map((name) => ({
      subdivisionCode,
      subdivisionName: name,
      countryName: getCountryName(WS_ISO3166_COUNTRY_BY_ALPHA_2[country]),
      countryCode: country,
    }))
);
export const WS_OVERRIDDEN_SUBDIVISIONS_BY_NAME = {
  ...keyBy(overriddenSubdivisionInfo, (s) =>
    normalizeLocationRef(s.subdivisionName)
  ),
};
